/**
 * site.js — behaviour loaded on every page
 *
 * Vanilla, no framework. Each block guards on its own selector so the
 * file is safe to load on pages that use none of it.
 */

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------
 | Header — solid on scroll, tucked away when scrolling down
 |------------------------------------------------------------------ */
function initHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    // Pages without a hero are locked solid by PHP.
    const alwaysSolid = header.dataset.solid === 'true';

    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
        const y = window.scrollY;

        if (!alwaysSolid) {
            header.classList.toggle('is-solid', y > 40);
        }

        // Tuck the bar away once past the fold, but only while scrolling down
        // and never while the mobile drawer is open.
        const scrollingDown = y > lastY && y > 320;
        const drawerOpen = document.body.classList.contains('nav-open');
        header.classList.toggle('is-tucked', scrollingDown && !drawerOpen);

        lastY = y;
        ticking = false;
    };

    window.addEventListener(
        'scroll',
        () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(update);
        },
        { passive: true }
    );

    update();
}

/* ------------------------------------------------------------------
 | Mobile navigation drawer
 |------------------------------------------------------------------ */
function initMobileNav() {
    const drawer = document.querySelector('[data-mobile-nav]');
    const overlay = document.querySelector('[data-nav-overlay]');
    const toggle = document.querySelector('[data-nav-toggle]');
    if (!drawer || !toggle) return;

    let lastFocused = null;

    const open = () => {
        lastFocused = document.activeElement;
        drawer.classList.add('is-open');
        document.body.classList.add('nav-open');
        toggle.setAttribute('aria-expanded', 'true');

        if (overlay) {
            overlay.classList.remove('opacity-0', 'pointer-events-none');
        }

        drawer.querySelector('[data-nav-close]')?.focus();
    };

    const close = () => {
        drawer.classList.remove('is-open');
        document.body.classList.remove('nav-open');
        toggle.setAttribute('aria-expanded', 'false');

        if (overlay) {
            overlay.classList.add('opacity-0', 'pointer-events-none');
        }

        lastFocused?.focus();
    };

    toggle.addEventListener('click', open);
    overlay?.addEventListener('click', close);
    drawer.querySelector('[data-nav-close]')?.addEventListener('click', close);

    // Any link inside the drawer closes it.
    drawer.querySelectorAll('a[href]').forEach((link) => {
        link.addEventListener('click', close);
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
            close();
        }
    });

    // Keep tab focus inside the drawer while it is open.
    drawer.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !drawer.classList.contains('is-open')) return;

        const focusable = drawer.querySelectorAll(
            'a[href], button:not([disabled]), input, select, textarea'
        );
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    });
}

/* ------------------------------------------------------------------
 | Scroll reveal
 |------------------------------------------------------------------ */
function initReveal() {
    const items = document.querySelectorAll('[data-reveal]');
    if (!items.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('is-revealed'));
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                entry.target.classList.add('is-revealed');
                observer.unobserve(entry.target);
            });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    items.forEach((item) => observer.observe(item));
}

/* ------------------------------------------------------------------
 | Hero cross-fade
 |------------------------------------------------------------------ */
function initHeroSlides() {
    const container = document.querySelector('[data-hero-slides]');
    if (!container) return;

    const slides = [...container.querySelectorAll('[data-hero-slide]')];
    const dots = [...document.querySelectorAll('[data-hero-dot]')];
    if (slides.length < 2) return;

    let index = 0;
    let timer = null;

    const show = (next) => {
        slides[index].classList.replace('opacity-100', 'opacity-0');
        dots[index]?.setAttribute('aria-selected', 'false');

        index = (next + slides.length) % slides.length;

        slides[index].classList.replace('opacity-0', 'opacity-100');
        dots[index]?.setAttribute('aria-selected', 'true');
    };

    const start = () => {
        if (prefersReducedMotion) return;
        stop();
        timer = window.setInterval(() => show(index + 1), 7000);
    };

    const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
    };

    dots.forEach((dot, position) => {
        dot.addEventListener('click', () => {
            show(position);
            start();
        });
    });

    // Do not burn cycles animating a hero nobody is looking at.
    document.addEventListener('visibilitychange', () => {
        document.hidden ? stop() : start();
    });

    start();
}

/* ------------------------------------------------------------------
 | Sticky mobile action bar — appears once the hero is behind you
 |------------------------------------------------------------------ */
function initMobileBar() {
    const bar = document.querySelector('[data-mobile-bar]');
    if (!bar) return;

    let ticking = false;

    const update = () => {
        bar.classList.toggle('translate-y-full', window.scrollY < 420);
        ticking = false;
    };

    window.addEventListener(
        'scroll',
        () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(update);
        },
        { passive: true }
    );

    update();
}

/* ------------------------------------------------------------------
 | Shared form behaviour — return leg, flight field, WhatsApp handoff
 |
 | Used by the quick form in the hero and by the full booking form.
 |
 | Shared through window.ztUtils rather than an ES import: site.js and
 | booking.js are separate Vite entries, and the PHP enqueue helper does
 | not pull in shared chunks.
 |------------------------------------------------------------------ */

/**
 * Show or hide the return date/time fields to match the trip type.
 *
 * @param {HTMLElement} scope Element containing the radios and fields.
 */
function bindTripType(scope) {
    const fields = scope.querySelector('[data-return-fields]');
    if (!fields) return;

    const radios = scope.querySelectorAll('input[name="trip_type"]');
    if (!radios.length) return;

    const sync = () => {
        const isReturn = [...radios].some((radio) => radio.checked && radio.value === 'return');
        fields.hidden = !isReturn;
    };

    radios.forEach((radio) => radio.addEventListener('change', sync));
    sync();
}

/**
 * Collect a form's values as a plain object.
 *
 * @param {HTMLFormElement} form
 * @returns {Record<string, string>}
 */
function readForm(form) {
    const data = {};
    new FormData(form).forEach((value, key) => {
        if (typeof value === 'string' && value.trim() !== '') {
            data[key] = value.trim();
        }
    });
    return data;
}

/**
 * Build a wa.me URL carrying the booking details.
 *
 * @param {Record<string, string>} data
 * @returns {string} Empty string when no WhatsApp number is configured.
 */
function whatsappLink(data) {
    const base = window.ztData?.whatsapp;
    if (!base) return '';

    const labels = {
        tour: 'Tour',
        pickup: 'Pickup',
        dropoff: 'Destination',
        date: 'Date',
        time: 'Time',
        return_date: 'Return date',
        return_time: 'Return time',
        trip_type: 'Trip',
        passengers: 'Passengers',
        flight_number: 'Flight',
        vehicle: 'Vehicle',
        name: 'Name',
        phone: 'Phone',
        notes: 'Notes',
    };

    const lines = ['Hi! I would like to book:'];

    Object.entries(labels).forEach(([key, label]) => {
        if (data[key]) lines.push(`${label}: ${data[key]}`);
    });

    // wa.me carries the message in the query string, so rebuild it wholesale.
    return `${base.split('?')[0]}?text=${encodeURIComponent(lines.join('\n'))}`;
}

function initQuickForm() {
    const form = document.querySelector('[data-quick-form]');
    if (!form) return;

    bindTripType(form);

    // Flight number is opt-in — most transfers are not from the airport.
    const toggle = form.querySelector('[data-toggle-flight]');
    const wrap = form.querySelector('#zt-flight-wrap');

    if (toggle && wrap) {
        toggle.addEventListener('click', () => {
            const open = wrap.hidden;
            wrap.hidden = !open;
            toggle.setAttribute('aria-expanded', String(open));
            if (open) wrap.querySelector('input')?.focus();
        });
    }

    // Keep the return date at or after the outbound date.
    const date = form.querySelector('input[name="date"]');
    const returnDate = form.querySelector('input[name="return_date"]');

    date?.addEventListener('change', () => {
        if (returnDate && date.value) returnDate.min = date.value;
    });

    // Hand the same values to WhatsApp instead of the booking page.
    form.querySelector('[data-whatsapp-submit]')?.addEventListener('click', () => {
        const url = whatsappLink(readForm(form));
        if (url) window.open(url, '_blank', 'noopener');
    });
}

// Made available to booking.js, which loads as its own bundle.
window.ztUtils = { bindTripType, readForm, whatsappLink };

/* ------------------------------------------------------------------
 | Boot
 |------------------------------------------------------------------ */
function init() {
    // Tells the inline head script that reveals are being handled.
    window.ztBooted = true;

    initHeader();
    initMobileNav();
    initReveal();
    initHeroSlides();
    initMobileBar();
    initQuickForm();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
